import { Users, Clock, Sparkles, ArrowRight } from "lucide-react";
import RichText from "@/components/RichText";
import { proxied } from "@/lib/api";

const ShravanGrid = ({ items = [], title, subtitle, kicker }) => {
  if (!items.length) return null;

  return (
    <section id="shravan-pujas" className="bg-[#F3EBE1] py-14 md:py-24">
      <div className="max-w-7xl mx-auto px-5 md:px-12">
        <div className="text-center mb-10 md:mb-14 max-w-3xl mx-auto">
          <RichText text={kicker || "Pioneers Since 2001"} as="div" className="text-[#8B2323] text-[11px] md:text-sm tracking-[0.4em] md:tracking-[0.5em] uppercase mb-3" />
          <RichText text={"Sacred Navratri Puja" || "Sacred Sankalp Pujas"} as="h2" className="font-serif text-3xl md:text-5xl tracking-tight text-[#2D2825] leading-tight" />
          {subtitle && (<RichText text={subtitle} as="p" className="mt-3 text-[#5C544F] text-sm md:text-base whitespace-pre-line" />)}
          <div className="h-[2px] w-16 bg-[#D4AF37] mt-5 mx-auto" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-7">
          {items.map((p) => {
            const href = p.inquiry_link || "https://rudralife.com/sankalp-pujas";
            return (
              <a
                key={p.id}
                href={href}
                target="_blank"
                rel="noreferrer"
                data-testid={`sankalp-puja-${p.id}`}
                data-cursor="puja"
                className="group bg-white rounded-2xl overflow-hidden border border-[rgba(45,40,37,0.08)] hover:shadow-[0_25px_50px_rgba(139,35,35,0.14)] transition-all hover:-translate-y-1 flex flex-col"
              >
                {/* Image with overlay name */}
                <div className="aspect-[4/3] overflow-hidden relative bg-[#faf6f0]">
                  <img
                    src={proxied(p.image)}
                    alt={p.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                  {p.month && (
                    <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-[#D4AF37] text-[#1A1412] text-[9px] font-semibold tracking-[0.3em] uppercase">
                      {p.month}
                    </div>
                  )}
                  <div className="absolute bottom-3 left-3 right-3">
                    <h3 className="font-serif text-lg md:text-xl text-white leading-tight drop-shadow-lg">
                      {p.name}
                    </h3>
                  </div>
                </div>

                {/* Body */}
                <div className="p-4 md:p-5 flex-1 flex flex-col">
                  {/* Icons row - pandit + timing */}
                  <div className="flex flex-wrap gap-3 mb-3">
                    {p.pandit_count > 0 && (
                      <div className="inline-flex items-center gap-1.5 text-xs text-[#5C544F]">
                        <span className="w-7 h-7 rounded-full bg-[#8B2323]/10 flex items-center justify-center">
                          <Users size={13} className="text-[#8B2323]" />
                        </span>
                        <span className="font-medium text-[#2D2825]">{p.pandit_count}</span>
                        <span className="text-[10px] tracking-wider uppercase">Pandits</span>
                      </div>
                    )}
                    {p.timing && (
                      <div className="inline-flex items-center gap-1.5 text-xs text-[#5C544F]">
                        <span className="w-7 h-7 rounded-full bg-[#D4AF37]/15 flex items-center justify-center">
                          <Clock size={13} className="text-[#B5952F]" />
                        </span>
                        <span className="text-[11px] font-medium text-[#2D2825] line-clamp-1">
                          <span className="text-[9px] tracking-widest uppercase text-[#D4AF37] mr-1">Muhurat:</span>
                          {p.timing}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* About the Puja — supports paragraph (preserves newlines) OR bullet points.
                       If admin ne about_format = 'bullets' set kiya hai, hum newlines par split karke bullet list dikhaate hain. */}
                  {p.long_desc && (
                    <div className="mb-3">
                      <div className="text-[9px] tracking-[0.35em] uppercase text-[#D4AF37] mb-1">About the Puja</div>
                      {p.about_format === "bullets" ? (
                        <ul className="space-y-1">
                          {p.long_desc.split("\n").map((s) => s.trim()).filter(Boolean).map((line, i) => (
                            <li key={i} className="flex gap-2 text-xs md:text-sm text-[#2D2825] leading-snug">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#8B2323] mt-1.5 flex-shrink-0" />
                              <span>{line}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs md:text-sm text-[#2D2825] leading-snug whitespace-pre-line">
                          {p.long_desc}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Prashad — same paragraph/bullets toggle */}
                  {p.prashad && (
                    <div className="mb-4">
                      <div className="flex items-center gap-1.5 text-[9px] tracking-[0.35em] uppercase text-[#8B2323] mb-1.5">
                        <Sparkles size={11} /> Prashad
                      </div>
                      {p.prashad_format === "bullets" ? (
                        <ul className="space-y-1">
                          {p.prashad.split("\n").map((s) => s.trim()).filter(Boolean).map((line, i) => (
                            <li key={i} className="flex gap-2 text-xs text-[#5C544F] leading-relaxed">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mt-1.5 flex-shrink-0" />
                              <span>{line}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-[#5C544F] leading-relaxed whitespace-pre-line">
                          {p.prashad}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Participate CTA */}
                  <div className="mt-auto pt-2">
                    <div className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-full bg-[#8B2323] text-white group-hover:bg-[#6B1A1A] text-[10px] tracking-[0.3em] uppercase transition-colors">
                      Participate Now <ArrowRight size={12} />
                    </div>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ShravanGrid;
